import { useState, useEffect, useRef } from 'react'
import Image from 'next/image'

interface LockScreenModalProps {
  isLocked: boolean;
  onUnlock: () => void;
}

export default function LockScreenModal({ isLocked, onUnlock }: LockScreenModalProps) {
  const [currentTime, setCurrentTime] = useState(new Date())
  const [isUnlocking, setIsUnlocking] = useState(false)
  const [sliderPosition, setSliderPosition] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const sliderRef = useRef<HTMLDivElement>(null)
  const thumbRef = useRef<HTMLDivElement>(null)
  const startXRef = useRef(0)
  const [weather] = useState({
    temp: '6°C',
    low: '1',
    high: '6',
    location: 'Swinton',
    condition: 'Partly Cloudy'
  })

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  // Add keyboard support for accessibility (desktop only)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isLocked || isMobile) return;
      
      // Space, Enter, or Right Arrow to unlock
      if (e.key === ' ' || e.key === 'Enter' || e.key === 'ArrowRight') {
        e.preventDefault();
        triggerUnlock();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLocked, isMobile]);

  const triggerUnlock = () => {
    if (sliderRef.current) {
      const sliderWidth = sliderRef.current.offsetWidth;
      setSliderPosition(sliderWidth - 80);
      setIsUnlocking(true);
      setTimeout(() => {
        onUnlock();
      }, 500);
    }
  };

  const handleStart = (clientX: number) => {
    setIsDragging(true)
    startXRef.current = clientX
    
    // Add active class for styling
    if (thumbRef.current) {
      thumbRef.current.classList.add('active')
    }
  }

  const handleMove = (clientX: number) => {
    if (!isDragging || !sliderRef.current) return
    
    const sliderWidth = sliderRef.current.offsetWidth
    const sliderRect = sliderRef.current.getBoundingClientRect()
    const relativeX = clientX - sliderRect.left
    
    // Calculate position relative to the slider
    const newPosition = Math.max(0, Math.min(relativeX - 24, sliderWidth - 60))
    
    setSliderPosition(newPosition)
    
    // Check if slider has been moved far enough to trigger unlock
    if (newPosition >= sliderWidth - 80) {
      triggerUnlock();
    }
  }

  const handleEnd = () => {
    setIsDragging(false)
    if (!isUnlocking) {
      // Animate back to start position
      setSliderPosition(0)
    }
    
    // Remove active class
    if (thumbRef.current) {
      thumbRef.current.classList.remove('active')
    }
  }

  // Add a double-click handler for desktop users
  const handleDoubleClick = () => {
    if (!isMobile) {
      triggerUnlock();
    }
  };

  // Move event handlers to a separate useEffect to avoid stale closures
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => handleMove(e.clientX)
    const handleTouchMove = (e: TouchEvent) => {
      if (e.cancelable) {
        e.preventDefault(); // Prevent scrolling while dragging
      }
      handleMove(e.touches[0].clientX);
    }
    const handleMouseUp = () => handleEnd()
    const handleTouchEnd = () => handleEnd()
    
    if (isDragging) {
      // Use capture phase to ensure we get all events
      window.addEventListener('mousemove', handleMouseMove, { capture: true })
      window.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true })
      window.addEventListener('mouseup', handleMouseUp, { capture: true })
      window.addEventListener('touchend', handleTouchEnd, { capture: true })
    }
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove, { capture: true })
      window.removeEventListener('touchmove', handleTouchMove, { capture: true })
      window.removeEventListener('mouseup', handleMouseUp, { capture: true })
      window.removeEventListener('touchend', handleTouchEnd, { capture: true })
    }
  }, [isDragging, handleMove, handleEnd])

  useEffect(() => {
    if (isLocked) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isLocked]);

  if (!isLocked) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] transition-all duration-1000"
      style={{ 
        touchAction: 'none', // Prevent browser's default touch actions
        userSelect: 'none' // Prevent text selection
      }}
      onTouchMove={(e) => {
        // Prevent default touch behavior on the modal
        if (isDragging && e.cancelable) {
          e.preventDefault();
        }
      }}
      onContextMenu={(e) => {
        // Prevent context menu from appearing on long press
        e.preventDefault();
      }}
    >
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <Image 
          src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/unlockbg.jpg?v=1740552960"
          alt="Lock screen background"
          fill
          priority
          quality={90}
          sizes="100vw"
          style={{ 
            objectFit: 'cover',
            objectPosition: 'center'
          }}
        />
        {/* Subtle overlay for better contrast */}
        <div className="absolute inset-0 bg-black/40"></div>
      </div>

      <div className={`absolute inset-0 flex flex-col justify-between p-8 transition-all duration-700 z-10 ${isUnlocking ? 'translate-y-[-100%] opacity-0' : ''}`}>
        {/* Logo - Larger and cleaner presentation */}
        <div className="w-full flex justify-center">
          <Image
            src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/df_corpo_logo.webp?v=1740682665"
            alt="DF5 Logo"
            width={180}
            height={180}
            priority
            className="object-contain"
            style={{ filter: 'drop-shadow(0 0 12px rgba(255,255,255,0.4))' }}
          />
        </div>
        
        {/* Top Container - Time and Weather */}
        <div className="w-full max-w-md mx-auto rounded-[2rem] relative overflow-hidden -mt-24">
          {/* Single backdrop blur layer */}
          <div className="absolute inset-0 backdrop-blur-xl"></div>
          
          {/* Single gradient background */}
          <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-black/30 rounded-[2rem]"></div>
          
          {/* Single border with glow - simplified animation */}
          <div className="absolute inset-0 rounded-[2rem] border border-white/10">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-[shine-slide_4s_ease-in-out_infinite]"></div>
          </div>
          
          {/* Content wrapper */}
          <div className="relative z-10 p-6 pt-3">
            {/* Time */}
            <div className="text-center mb-8">
              <div className="text-7xl font-light text-white mb-2 tracking-wider"
                   style={{ textShadow: '0 0 20px rgba(255,255,255,0.5), 0 0 40px rgba(255,255,255,0.2)' }}>
                {currentTime.getHours().toString().padStart(2, '0')}:
                {currentTime.getMinutes().toString().padStart(2, '0')}
              </div>
              <div className="text-white/80 text-lg font-light tracking-widest">
                {currentTime.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </div>
            </div>
            
            {/* Weather - simplified */}
            <div className="flex items-center justify-between bg-white/5 rounded-lg p-4 backdrop-blur-md">
              <div className="text-white">
                <div className="text-4xl font-light tracking-wide">{weather.temp}</div>
                <div className="text-sm opacity-80 font-light">{weather.condition}</div>
                <div className="text-xs opacity-60">{weather.location}</div>
              </div>
              <div className="w-20 h-20 relative">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-orange-400/80 to-yellow-600/80 blur-sm"></div>
                <div className="absolute inset-2 rounded-full bg-gradient-to-br from-yellow-300 to-orange-500"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Container */}
        <div className="w-full max-w-md mx-auto">
          {/* Bottom Slider */}
          <div 
            ref={sliderRef}
            className="w-full h-16 relative overflow-hidden rounded-full cursor-pointer backdrop-blur-xl slider-track"
            onMouseDown={(e) => handleStart(e.clientX)}
            onTouchStart={(e) => {
              e.stopPropagation(); // Prevent other touch handlers
              handleStart(e.touches[0].clientX);
            }}
            onDoubleClick={handleDoubleClick}
            onClick={(e) => {
              // Allow clicking anywhere on the slider track to move the thumb
              if (!isDragging && sliderRef.current) {
                const sliderRect = sliderRef.current.getBoundingClientRect();
                const clickX = e.clientX - sliderRect.left;
                
                // Only process if click is ahead of current position
                if (clickX > sliderPosition + 24) {
                  // If click is near the end, trigger unlock
                  if (clickX > sliderRect.width - 80) {
                    triggerUnlock();
                  } else {
                    // Otherwise move to the clicked position
                    setSliderPosition(Math.max(0, clickX - 24));
                  }
                }
              }
            }}
          >
            {/* Slider background effects */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60"></div>
            <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent"></div>
            <div className="absolute inset-0 border border-white/20 rounded-full overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-[shine-slide_3s_linear_infinite]"></div>
            </div>

            {/* Enhanced thumb with glow effect */}
            <div 
              ref={thumbRef}
              className="absolute top-2 bottom-2 left-2 w-12 rounded-full transition-transform duration-200 cursor-grab active:cursor-grabbing slider-thumb"
              style={{ 
                transform: `translateX(${sliderPosition}px)`,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.9), rgba(255,255,255,0.2))',
                boxShadow: '0 0 15px rgba(255,255,255,0.3)',
                touchAction: 'none',
                willChange: 'transform',
                zIndex: 10
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                handleStart(e.clientX);
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleStart(e.touches[0].clientX);
              }}
            >
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/90 to-transparent"></div>
            </div>

            {/* Slider text */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-white/70 text-sm font-light tracking-[0.2em] uppercase"
                    style={{ textShadow: '0 0 10px rgba(255,255,255,0.3)' }}>
                {isMobile ? 'slide to unlock' : 'slide to unlock'}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        .slider-thumb:hover {
          box-shadow: 0 0 20px rgba(255,255,255,0.5);
        }
        
        .slider-thumb.active {
          cursor: grabbing;
          animation: thumb-glow 2s infinite;
        }
        
        @keyframes thumb-glow {
          0%, 100% { box-shadow: 0 0 15px rgba(255,255,255,0.3); }
          50% { box-shadow: 0 0 25px rgba(255,255,255,0.5); }
        }
      `}</style>
    </div>
  )
} 